<template lang="pug">
form.inline-block.password-meter.prose-stack.w-full.max-w-md(
  :class="{ 'mx-auto text-center': props.screen.center }"
  autocomplete="off"
)
  //- expose this string for the translate script
  //- text="Try a password"
  p.w-full
    input.text-2xl.tracking-wider.w-full.transition.p-4.resize-none.ring-1.focus_ring-4.shadow-sm.rounded-md.dark_bg-gray-900(
      class="dark_bg-gray-900/50 ring-gray-800/20 dark_ring-gray-200/20 focus_ring-sky-500/50 dark_focus_ring-sky-400/40 dark_highlight-white/5"
      :placeholder="hasTranslation('Try a password')"
      type="text"
      v-model="password"
    )

  .password-meter-bar.my-4.h-3.w-full.dark_ring-1(
    :class="activeColor"
    class="bg-gray-400/20 dark_bg-black/40 dark_ring-gray-200/10"
  )
    .password-meter-bar-fill.bg-primary-500.transition-all.duration-1000.h-full.max-w-full(
      :style="{ width: `${(score / 4) * 100}%` }"
    )

  .collapsed-items.auto-alpha-0.auto-alpha-fast(
    :class="{ 'auto-alpha-1': score > 0 }"
    v-if="props.screen.feedback && !audioDoc"
  )
    .collapsed-item.auto-alpha-0(
      :class="{ 'auto-alpha-1': +activeFeedback === +k }"
      v-for="[k, s] in Object.entries(props.screen.feedback)"
    )
      uc-editable(
        :edit-path="`${editPath}.feedback.${k}.text`"
        :text="s.text"
        :k="`${props.screen.id}.feedback.${k}.text`"
        v-if="s"
      )
</template>

<script setup lang="ts">
import { computed, ref, watch, watchEffect, inject } from 'vue';
import { setCompleted } from '../lib/SCORM';
import { useCompletables } from '../stores/useLockables';
import bugAlert from '../lib/bugAlert';
import useSuspend from '../lib/useSuspend';
import useTranslations from '../stores/useTranslations';

const audioDoc = inject('audioDoc') as boolean;
const suspend = useSuspend();
const { translations } = useTranslations();

const props = defineProps<{
  editPath: string;
  screen: UcScreenRequiredId & {
    feedback?: {
      0?: UcContent;
      1?: UcContent;
      2?: UcContent;
      3?: UcContent;
      4?: UcContent;
    };
  };
}>();

useCompletables(props.screen.id);

const password = ref('');
const score = ref(0);

// lazy load with code splitting
const zxcvbnImport = import('zxcvbn');

/**
 * From a list of possible scores, find the highest score that is less than or equal to the current score
 */
const findClosestScore = (scores: number[] | string[]) =>
  +scores.reduce(
    (prev, curr) =>
      +curr <= Math.floor(score.value) && +curr > +prev ? +curr : +prev,
    -1
  );

const activeFeedback = computed(() =>
  findClosestScore(Object.keys(props.screen.feedback || {}))
);

const activeColor = computed(() => {
  switch (findClosestScore([0, 1, 2, 3, 4])) {
    case 0:
      return 'primary-red';
    case 1:
      return 'primary-orange';
    case 2:
      return 'primary-yellow';
    case 3:
      return 'primary-green';
    case 4:
      return 'primary-green';
    default:
      return 'primary-green';
  }
});

function hasTranslation(key: string | number, fallback: string | number = key) {
  // get the translation if it exists
  let maybeTranslated =
    translations?.[suspend.locale]?.[key] ||
    translations?.[suspend.locale]?.[fallback];
  return maybeTranslated || key;
}

watch(password, async p => {
  const zxcvbn = (await zxcvbnImport).default;
  const result = zxcvbn(p);
  const g = result.guesses_log10;
  // polynomial fit to zxcvbn scores from 0 to 4
  const s = 0.0131 * g * g + 0.268 * g;
  score.value = s;
});

watch(score, s => {
  if (s >= 4) setCompleted(props.screen.id);
});

// validate feedback props are 0, 1, 2, 3, or 4
watchEffect(() => {
  const feedback = props.screen.feedback;
  if (!feedback) return;
  const keys = Object.keys(feedback);
  const valid = keys.every(k => [0, 1, 2, 3, 4].includes(+k));

  if (!valid)
    bugAlert(
      `Password meter feedback for #{${props.screen.id}} must be an object with keys from 0 to 4.\n` +
        `Found ${keys.join(', ')}`
    );
});
</script>

<style>
.password-meter .uc-content strong {
  color: var(--tw-prose-text);
}
</style>
